import Grid from "@mui/material/Grid2";
import { Box, Typography } from "@mui/material";
import { intl } from "LocaleWrapper";
import { AgreementCondition } from "view/agreementconditions/types/agreementcondition";
import { ReactNode } from "react";
import { ColumnProps, Table } from "@cofano/cck";
import { LocationLink, ProductLink, ShipLink } from "utils/LinkUtils";
import { AgreementConditionType } from "__generated__/graphql";
import { DataFormat } from "utils";
import { ChecklistOutlined } from "@mui/icons-material";
import { useFrontendTable } from "components/table";

type AgreementConditionDetailsProps = {
	conditions: AgreementCondition[];
}

type DetailsField = {
	id: string;
	title: string;
	visible: (condition: AgreementCondition) => boolean;
	render: (row: AgreementCondition) => ReactNode;
}

const AgreementConditionDetails = ({ conditions }: AgreementConditionDetailsProps) => {
	const { tableProps } = useFrontendTable({
		id: "agreementConditions",
		defaultSize: "COMPACT",
		content: conditions,
		saveForOrganizationEnabled: false,
	});

	const AgreementConditionDetailsFields: DetailsField[] = [
		{
			id: "conditions.type",
			title: intl.formatMessage({ id: "TYPE" }),
			visible: () => true,
			render: (row) => intl.formatMessage({ id: `AGREEMENT_CONDITION_TYPE_${row.type}` }),
		},
		{
			id: "conditions.location",
			title: intl.formatMessage({ id: "LOCATION" }),
			visible: c => c.type === AgreementConditionType.Location,
			render: (row) => row.location && <LocationLink location={row.location} />,
		},
		{
			id: "conditions.locationActionType",
			title: intl.formatMessage({ id: "LOCATION_ACTION_TYPE" }),
			visible: c => c.type === AgreementConditionType.Location,
			render: (row) => row.locationActionType && intl.formatMessage({ id: row.locationActionType }),
		},
		{
			id: "conditions.fromLocation",
			title: intl.formatMessage({ id: "ROUTE_FROM" }),
			visible: c => c.type === AgreementConditionType.Route,
			render: (row) => row.fromLocation && <LocationLink location={row.fromLocation} />,
		},
		{
			id: "conditions.toLocation",
			title: intl.formatMessage({ id: "ROUTE_TO" }),
			visible: c => c.type === AgreementConditionType.Route,
			render: (row) => row.toLocation && <LocationLink location={row.toLocation} />,
		},
		{
			id: "conditions.product",
			title: intl.formatMessage({ id: "PRODUCT" }),
			visible: c => c.type === AgreementConditionType.Product,
			render: (row) => row.product && <ProductLink product={row.product} />,
		},
		{
			id: "conditions.empty",
			title: intl.formatMessage({ id: "EMPTY" }),
			visible: c => c.type === AgreementConditionType.ContainerFull,
			render: (row) => row.empty !== undefined ? DataFormat.booleanAsText(row.empty) : "",
		},
		{
			id: "conditions.ship",
			title: intl.formatMessage({ id: "SHIP" }),
			visible: c => c.type === AgreementConditionType.Ship,
			render: (row) => row.ship && <ShipLink ship={row.ship} />,
		},
		{
			id: "conditions.waterLevelLocation",
			title: intl.formatMessage({ id: "LOCATION" }),
			visible: c => c.type === AgreementConditionType.WaterLevel,
			render: (row) => row.waterLevelLocation,
		},
		{
			id: "conditions.waterLevelMin",
			title: intl.formatMessage({ id: "TIER_MINIMUM_WATER_LEVEL" }),
			visible: c => c.type === AgreementConditionType.WaterLevel,
			render: (row) => row.waterLevelMin != null ? `${row.waterLevelMin} m` : "",
		},
		{
			id: "conditions.waterLevelMax",
			title: intl.formatMessage({ id: "TIER_MAXIMUM_WATER_LEVEL" }),
			visible: c => c.type === AgreementConditionType.WaterLevel,
			render: (row) => row.waterLevelMax != null ? `${row.waterLevelMax} m` : "",
		},
		{
			id: "conditions.reeferCheckType",
			title: intl.formatMessage({ id: "REEFER_CHECK_TYPE" }),
			visible: c => c.type === AgreementConditionType.ReeferCheckType,
			render: (row) => row.reeferCheckType != null ?
				intl.formatMessage({ id: `REEFER_CHECK_TYPE_${row.reeferCheckType}` })  : "",
		},
	];

	const columns: ColumnProps[] = AgreementConditionDetailsFields.filter(x => conditions.some(x.visible));

	return (
		<Grid container size={12}>
			<Grid size={6}>
				<Box sx={{  display: "flex", alignItems: "center" }}>
					<ChecklistOutlined fontSize="small" />
					<Typography
						color="textPrimary"
						fontSize={13}
						fontWeight="medium"
						marginLeft={1}>
						{intl.formatMessage({ id: "AGREEMENT_CONDITIONS" })}
					</Typography>
				</Box>
			</Grid>
			<Grid size={12}>
				<Table {...tableProps} columns={columns} disableFooter />
			</Grid>
		</Grid>
	);

};

export default AgreementConditionDetails;
